<template>
  <section class="mt-2 space-y-4 bg-base-100 p-4">
    <div class="flex flex-wrap items-end gap-2">
      <label class="form-control">
        <span class="label-text">User ID</span>
        <input v-model.trim="userId" class="input input-bordered input-sm" autocomplete="off" maxlength="24" />
      </label>
      <label class="form-control">
        <span class="label-text">Vai trò trong chiến dịch</span>
        <select v-model="memberRole" class="select select-bordered select-sm">
          <option value="manager">Manager</option>
          <option value="operator">Operator</option>
          <option value="viewer">Viewer</option>
        </select>
      </label>
      <button class="btn btn-primary btn-sm" :disabled="!isValidUserId || saving" @click="saveMember">
        {{ saving ? "Đang lưu…" : "Thêm / cập nhật" }}
      </button>
    </div>

    <p v-if="error" role="alert" class="text-error">{{ error }}</p>
    <div v-if="loading" class="loading loading-spinner" aria-label="Đang tải"></div>
    <div v-else-if="members.length === 0" class="text-base-content/70">Chưa có thành viên.</div>
    <div v-else class="overflow-x-auto">
      <table class="table table-sm">
        <thead><tr><th>User ID</th><th>Vai trò</th><th></th></tr></thead>
        <tbody>
          <tr v-for="member in members" :key="member.user_id">
            <td class="font-mono">{{ member.user_id }}</td><td>{{ member.role }}</td>
            <td class="text-right"><button class="btn btn-ghost btn-xs text-error" :disabled="saving" @click="removeMember(member.user_id)">Gỡ</button></td>
          </tr>
        </tbody>
      </table>
    </div>
    <button class="btn btn-sm" :disabled="loading" @click="loadMembers">Làm mới</button>
  </section>
</template>

<script setup lang="ts">
import { axios_instance } from "@/api/api";
import { use_campaign_detail } from "@/components/Campaign/useCampaignDetail";
import { computed, onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";

interface Membership {
  campaign_id: string;
  user_id: string;
  role: "manager" | "operator" | "viewer";
  created_at: string;
}

const campaign = use_campaign_detail();
const userId = ref("");
const memberRole = ref<Membership["role"]>("viewer");
const members = ref<Membership[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
let loadVersion = 0;
const isValidUserId = computed(() => /^[a-fA-F0-9]{24}$/.test(userId.value));

const apiError = (err: unknown, fallback: string) => {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  return typeof data?.error === "string" ? data.error : typeof data?.message === "string" ? data.message : fallback;
};

const loadMembers = async () => {
  const version = ++loadVersion;
  const campaignId = campaign.id;
  loading.value = true;
  error.value = "";
  try {
    const response = await axios_instance.get<{ members: Membership[] }>(`/api/v1/view/campaign/${campaignId}/members`);
    if (version === loadVersion) members.value = response.data.members ?? [];
  } catch (err) {
    if (version === loadVersion) error.value = apiError(err, "Không thể tải thành viên chiến dịch. Vui lòng thử lại.");
  } finally {
    if (version === loadVersion) loading.value = false;
  }
};

const saveMember = async () => {
  if (!isValidUserId.value) {
    toast.error("User ID phải là ObjectID gồm 24 ký tự hệ thập lục phân.");
    return;
  }
  saving.value = true;
  error.value = "";
  try {
    await axios_instance.put(`/api/v1/view/campaign/${campaign.id}/members`, { user_id: userId.value, role: memberRole.value });
    userId.value = "";
    await loadMembers();
  } catch (err) {
    toast.error(apiError(err, "Không thể lưu thành viên. Vui lòng kiểm tra quyền truy cập và thử lại."));
  } finally {
    saving.value = false;
  }
};

const removeMember = async (id: string) => {
  if (!confirm(`Bạn có chắc chắn muốn gỡ thành viên ${id} khỏi chiến dịch?`)) return;
  saving.value = true;
  error.value = "";
  try {
    await axios_instance.delete(`/api/v1/view/campaign/${campaign.id}/members/${encodeURIComponent(id)}`);
    await loadMembers();
  } catch (err) {
    error.value = apiError(err, "Không thể gỡ thành viên. Vui lòng kiểm tra quyền truy cập và thử lại.");
  } finally {
    saving.value = false;
  }
};

onMounted(loadMembers);
watch(() => campaign.id, loadMembers);
</script>
